import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import Search from "pages/Parts/Search";
import { PGET } from "helpers/api";
import DatePicker from "react-datepicker";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { DebounceInput } from "react-debounce-input";

function Index() {
	const URL = "/services/admin/api/unconfirmed-payments-pageList";
	const { t } = useTranslation();

	const [data, setData] = useState([]);
	const [startDate, setStartDate] = useState(null);
	const [endDate, setEndDate] = useState(null);


	useEffect(() => {
		getData();
	}, []);

	async function getData() {
		const response = await PGET(
			"/services/admin/api/pos-fees/external-service-fees",
			{},
			{ loader: true }
		);
		if (response) {
			setData(response.data);
		}
	}

	const [filter, setFilter] = useState("");

	async function search(searchValue) {
		setFilter(searchValue);
		if (searchValue.length > 0) {
			const lower = searchValue.toLowerCase();
			const filtered = data.filter(item =>
				`${item.posId}`.toLowerCase().includes(Number(lower)) ||
				`${item.login}`.toLowerCase().includes(lower)
			);
			setData(filtered);
		} else {
			getData();
		}
	}

	const visibleData = useMemo(() => {
		if (!filter) return data;

		const q = filter.toLowerCase();
		return data.filter(item =>
			`${item.posId}`.toLowerCase().includes(q) ||
			`${item.login}`.toLowerCase().includes(q)
		);
	}, [data, filter]);


	function getFeeOffset(key) {
		if (key === "feeCurrentMonth") return 0;
		const m = key.match(/fee(\d+)Months?Ago/);
		return m ? parseInt(m[1], 10) : 0;
	}

	function formatFeeKeyToDate(key) {
		const now = new Date();
		const offset = getFeeOffset(key);
		return new Date(now.getFullYear(), now.getMonth() - offset, 1);
	}



	function getSortedFeeKeys(row = {}) {
		return Object.keys(row)
			.filter((k) => k.startsWith("fee"))
			.sort((a, b) => getFeeOffset(a) - getFeeOffset(b));
	}

	function formatFeeKeyToMonthExcel(key) {
		const now = new Date(); // 18.06.2025 (today)
		const offset = getFeeOffset(key);
		const target = new Date(now.getFullYear(), now.getMonth() - offset, 1);

		const monthNamesRu = [
			"Январь",
			"Февраль",
			"Март",
			"Апрель",
			"Май",
			"Июнь",
			"Июль",
			"Август",
			"Сентябрь",
			"Октябрь",
			"Ноябрь",
			"Декабрь",
		];
		return `${monthNamesRu[target.getMonth()]} ${target.getFullYear()}`;
	}

	function downloadExcel() {
		if (!data.length) return;

		const feeKeys = getSortedFeeKeys(data[0]); // UPDATED

		/* 1️⃣ – sarlavha */
		const header = [
			"ID",
			"Login",
			"Holati",
			"Turi",
			"Bosh to‘lov",
			...feeKeys.map(formatFeeKeyToMonthExcel), // UPDATED
		];

		/* 2️⃣ – qatorlar */
		const body = data.map((row, i) => [
			row.posId,
			row.login || "",
			row.activated ? "Aktiv" : "Neaktiv",
			row.comment || "",
			row.signOnFee || 0,
			...feeKeys.map((k) => row[k] ?? 0), // UPDATED
		]);

		/* 3️⃣ – Excel yaratish va yuklab berish */
		const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, "Report");

		const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
		const file = new Blob([excelBuffer], { type: "application/octet-stream" });
		saveAs(file, "OFD_tolovlari.xlsx");
	}
	const feeKeys = data.length ? getSortedFeeKeys(data[0]) : [];
	const visibleFeeKeys = useMemo(() => {
		if (!startDate || !endDate || !feeKeys.length) return feeKeys;

		const sorted = [...feeKeys].filter(key => {
			const date = formatFeeKeyToDate(key);
			return date >= startDate && date <= endDate;
		});

		// Sort by offset so that months are in chronological order
		return sorted.sort((a, b) => getFeeOffset(a) - getFeeOffset(b));
	}, [startDate, endDate, feeKeys]);


const searchRef = useRef(null);
	useEffect(() => {
		const handleKeyPress = (e) => {
			const active = document.activeElement;
			const isEditable =
				active &&
				(active.tagName === "INPUT" || active.tagName === "TEXTAREA") &&
				!active.disabled &&
				!active.readOnly;

			if (!isEditable) {
				// Agar hech qanday input fokusda bo'lmasa
				searchRef.current.focus();
			}
		};

		document.addEventListener("keydown", handleKeyPress);

		return () => {
			document.removeEventListener("keydown", handleKeyPress);
		};
	}, []);

	return (
		<>
			<style>
				{`
			/* Sticky ustunlar */
			.sticky-col {
				position: sticky;
				background-color: white !important;
				white-space: nowrap;
				z-index: 3;
			}

			/* 1: ID */
			.sticky-col-1 {
				left: 0;
				min-width: 50px;
				z-index: 5;
				padding: 4px 8px;
			}

			/* 2: Login */
			.sticky-col-2 {
				left: 50px;
				min-width: 110px;
				z-index: 5;
				padding: 4px 8px;
			}

			/* Sarlavhalar (thead) */
			.table thead th {
				position: sticky;
				top: 0;
				background-color: white !important;
				white-space: nowrap;
				z-index: 10;
				padding: 4px 8px;
				font-size: 13px;
			}

			/* Hujayralar orasidagi joyni kamaytirish */
			.table td {
				padding: 4px 8px !important;
				font-size: 13px;
				background-color: white;
				vertical-align: middle;
			}

			/* Status dumaloq */
			.status-dot {
				display: inline-block;
				width: 10px;
				height: 10px;
				border-radius: 50%;
				margin-right: 6px;
			}

			.status-active {
				background-color: #2CA67A; /* yashil */
			}

			.status-inactive {
				background-color: #dc3545; /* qizil */
			}
			`}
			</style>



			<div className="card">
				<div className="card-body">
					<div className="d-flex mb-3 justify-content-between">
						<div className="me-2">
							<div className="position-relative me-2">
								<i className="uil-search input-search-icon"></i>
								<DebounceInput
									inputRef={searchRef}
									type="text"
									className="form-control"
									placeholder={t('SEARCH') + '...'}
									minLength={1}
									debounceTimeout={1000}
									value={filter}
									onChange={(e) => search(e.target.value)}
								/>

							</div>
						</div>
						<div className="d-flex gap-3 mb-3 align-items-end">
							<div>
								<DatePicker
									selected={startDate}
									onChange={(date) => setStartDate(date)}
									dateFormat="MMMM yyyy"
									showMonthYearPicker
									className="form-control"
									placeholderText={t('boshlanish oy')}
								/>
							</div>
							<div>
								<DatePicker
									selected={endDate}
									onChange={(date) => setEndDate(date)}
									dateFormat="MMMM yyyy"
									showMonthYearPicker
									className="form-control"
									minDate={startDate}
									placeholderText={t('tugash oy')}
								/>
							</div>
						</div>


						<div>
							<button onClick={downloadExcel} className="btn btn-success">
								{t('Excelga yuklash')}
							</button>
						</div>
					</div>

					<div style={{ maxHeight: '550px', overflow: 'auto', position: 'relative' }}>
						<table className="table mb-0" style={{ maxHeight: '550px', overflow: 'auto', position: 'relative', whiteSpace: 'nowrap', borderCollapse: 'collapse', width: '100%' }}>
							<thead>
								<tr>
									<th className="sticky-col sticky-col-1" style={{ zIndex: 99 }}>{t('ID')}</th>
									<th className="sticky-col sticky-col-2" style={{ zIndex: 99 }}>{t('LOGIN')}</th>
									<th>{t('STATUS')}</th>
									<th>{t('TYPE')}</th>
									<th style={{ textAlign: "center" }}>{t('MAIN_PAY')}</th>
									{/* oylar */}
									{visibleFeeKeys.map((key) => {
										const formatted = formatFeeKeyToMonthExcel(key);
										const [month, year] = formatted.split(" ");
										return (
											<th key={key} style={{ textAlign: "center" }}>{month} <br /> {year}</th>
										)
									})}

								</tr>
							</thead>

							<tbody>
								{visibleData.map((item, index) => (
									<tr key={index}>
										<td className="sticky-col sticky-col-1">{item.posId}</td>
										<td className="sticky-col sticky-col-2">{item.login}</td>
										<td>
											<span className={`status-dot ${item.activated ? 'status-active' : 'status-inactive'}`}></span>
										</td>
										<td>{item.comment}</td>
										<td style={{ textAlign: "center" }}>{formatMoney(item.signOnFee || 0)}</td>
										{visibleFeeKeys.map((k) => (
											<td key={k} style={{ textAlign: "center" }}>{formatMoney(item[k] || 0)}</td>
										))}

									</tr>
								))}
							</tbody>


							<tfoot>
								<tr
									style={{
										position: 'sticky',
										bottom: 0,
										background: '#fff',
										fontWeight: 'bold',
										borderTop: '2px solid #000',
										zIndex: 2,
									}}
								>
									<td style={{ fontWeight: 'bold' }}>{t('TOTAL')}:</td>
									<td></td>
									<td></td>
									<td></td>

									{/* MAIN_PAY summasi */}
									<td style={{ textAlign: "center", fontWeight: 'bold' }}>
										{formatMoney(data.reduce((sum, item) => sum + (item.signOnFee || 0), 0))}
									</td>

									{/* Har bir oy uchun summa */}
									{visibleFeeKeys.map((k) => (
										<td key={k} style={{ textAlign: "center", fontWeight: 'bold' }}>
											{formatMoney(data.reduce((sum, item) => sum + (item[k] || 0), 0))}
										</td>
									))}

								</tr>
							</tfoot>
						</table>
					</div>

				</div>
			</div>
		</>
	)
}

export default Index





export function formatMoney(amount, thousands = " ") {
	try {
		// NaN va manfiy sonlarni nazorat qilish
		amount = Math.floor(Number(amount) || 0);
		const negativeSign = amount < 0 ? "-" : "";

		let i = Math.abs(amount).toString();
		let j = (i.length > 3) ? i.length % 3 : 0;

		return negativeSign +
			(j ? i.substr(0, j) + thousands : '') +
			i.substr(j).replace(/(\d{3})(?=\d)/g, "$1" + thousands);
	} catch (e) {
		console.log("formatMoney error:", e);
		return amount;
	}
}